import { Dispatch, SetStateAction, useEffect, useRef, useState, useContext } from 'react';
import { Link } from 'react-router-dom';
import { PROVIDER_SOURCES, DEFAULT_EMPTY_VALUE } from '../../constants/companyReport';
import axiosInstance from 'src/services/axiosInstance.service';

import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import * as screeningService from 'src/services/screening.service';
import * as notify from 'src/services/notification.service';
import { AppContext } from 'src/utils';

import LikelyHood from './LikelyHood';
import { getMatchByName } from 'src/services/companyReportDetail.service';
import CustomerRiskAssessment from './CustomerRiskAssessment';
import FinancialInformation from './companyDetails/FinancialInformation';
import RelevantDocuments from './companyDetails/RelevantDocuments';
import GroupStructure from './companyDetails/GroupStructure';
import CreditSummary from './companyDetails/CreditSummary';
import AdditionalInformation from './companyDetails/AdditionalInformation';
import Shareholder from './companyDetails/Shareholder';
import Director from './companyDetails/Director';
import OwnershipStructure from './companyDetails/OwnershipStructure';
import { sortScreeningResult } from 'src/utils/sort-screening-result.utils';
import { EntityType } from 'src/enums';
import { ICRADetails } from 'src/types/ICra';

interface Props {
  detailData: any;
  updateCRADetails: Dispatch<SetStateAction<ICRADetails>>;
  addDownloadPdfButton: Dispatch<SetStateAction<boolean>>;
  setDetailIsLoading: Dispatch<SetStateAction<boolean>>;
  updateScreenedEntities: Dispatch<SetStateAction<any>>;
  craDefaultValues: any;
}

const useInterval = (callback: () => void, delay: number | null) => {
  const savedCallback = useRef(callback);

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    function tick() {
      savedCallback.current();
    }

    if (delay !== null) {
      const id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
  }, [delay]);
};

const CompanyDetails = ({
  detailData,
  updateCRADetails,
  setDetailIsLoading,
  updateScreenedEntities,
  addDownloadPdfButton,
  craDefaultValues,
}:
Props) => {
  const { currentUser } = useContext(AppContext);
  let [screenedIndividualEntities, setScreenedIndividualEntities] = useState<any[]>([]);
  let [screenedBusinessEntities, setScreenedBusinessEntities] = useState<any[]>([]);
  let [allScreenedEntities, setAllScreenedEntities] = useState<any[]>([]);
  const [screeningAlerts, setScreeningAlerts] = useState([]);
  const [companyReportHasLoaded, setcompanyReportHasLoaded] = useState(false);

  const [screeningIsLoading, setScreeningIsLoading] = useState<boolean>(false);

  let [jobStatusDelay, setJobStatusDelay] = useState<any>(5000);

  const beneficialOwners = detailData?.companyProfile?.companyProfile?.beneficialOwners ?? [];
  const beneficialOwnersRelations = detailData?.companyProfile?.companyProfile?.beneficialOwnersRelations ?? [];

  const [activeIndex, setActiveIndex] = useState('');

  const handleOnClick = (index: any) => {
    setActiveIndex((i) => (i === index ? '' : index));
  };

  const getJobsStatus = async () => {
    let businessJobId = detailData?.screenedBusinesses?.jobId;
    let individualJobId = detailData.screenedIndividuals?.jobId;
    let jobIds = '';
    if (businessJobId) {
      jobIds = `${businessJobId}`;
    }

    if (businessJobId && individualJobId) {
      jobIds = `${jobIds},${individualJobId}`;
    }

    if (!jobIds || jobIds === '') {
      console.log('job not available!', businessJobId, individualJobId);
      return false;
    }

    try {
      const jobsStatusResponse = await axiosInstance.post(
        `customer/${currentUser?.customer_id}/user/${currentUser?.id}/jobs/status`,
        { jobIds },
      );
      const jobsInProgress = jobsStatusResponse?.data?.data?.jobStatuses?.filter(
        (jobs: any) => jobs.status === 'InProgress' || jobs.status === 'Pending',
      );

      //If jobs are not in progress then fetch and display entity details.
      if (!jobsInProgress?.length) {
        console.log('stopping job status check.');
        setJobStatusDelay(null);
        setScreeningIsLoading(true);

        try {
          const businessJobId = detailData?.screenedBusinesses?.jobId;
          if (businessJobId) {
            const businessAPIResult = await screeningService.getJobEntities(
              businessJobId,
              undefined,
              '',
              10,
            );
            const businessScreeningResults = businessAPIResult?.data?.data?.results ?? [];

            setScreenedBusinessEntities(
              businessScreeningResults.map((item: any) => ({
                ...item,
                entity_type: EntityType.Business,
              })),
            );
          }
        } catch (err: any) {
          console.log('[E] Error fetching business screening results', err);
          if (err.response.data.error?.message) {
            notify.error(err.response.data.error.message);
          } else {
            notify.error('Error fetching business screening results');
          }
        } finally {
          setDetailIsLoading(false);
        }

        if (individualJobId) {
          try {
            const individualJobId = detailData?.screenedIndividuals?.jobId;
            if (individualJobId) {
              const indAPIResults = await screeningService.getJobEntities(
                individualJobId,
                undefined,
                '',
                10,
              );

              setScreenedIndividualEntities(indAPIResults.data.data?.results ?? []);
            }
          } catch (err: any) {
            console.log('[E] Error fetching individuals screening results', err);

            if (err?.response?.data?.error?.message) {
              notify.error(err.response.data.error.message);
            } else {
              notify.error('Error fetching individual screening results');
            }
          }
        }
        // making sure at least one job id is available before hitting alerts API for it
        if (detailData?.screenedIndividuals?.jobId || detailData?.screenedBusinesses?.jobId) {
          const alertsForCompanyScreening = await fetchScreeningJobsAlerts([
            detailData?.screenedIndividuals?.jobId || '',
            detailData?.screenedBusinesses?.jobId || '',
          ]);
          // keeping it here for debugging purposes
          console.log('alertsForCompanyScreening', alertsForCompanyScreening);
        }
        // to indicate that post processing activities can be instantiated e.g. CRA computation
        setcompanyReportHasLoaded(true);
        setDetailIsLoading(false);
      }
      return jobsStatusResponse;
    } catch (err) {
      setScreeningIsLoading(false);
      notify.error('Error getting job status');
      console.error(err);
      return [];
    } finally {
      setScreeningIsLoading(false);
    }
  };

  const fetchScreeningJobsAlerts = async (jobIds: string[]) => {
    try {
      const alertsResponse = await screeningService.fetchAlertsByJobIds(jobIds);
      const data = alertsResponse.data.data;
      console.log(`Alerts returned from API for company search = ${data?.results?.length}`);
      setScreeningAlerts(data.results);
      return data.results;
      // @ts-ignore
    } catch (error: any) {
      console.log({ error });
      if (!error.response.handled) {
        if (error?.response?.data?.error?.message) notify.error(error.response.data.error.message);
      }
    }
  };

  useEffect(() => {
    updateScreenedEntities({
      screenedBusinessEntities: screenedBusinessEntities,
      screenedIndividualEntities,
    });

    // combine all entities and apply sorting
    const allScreenings = screenedIndividualEntities.concat(screenedBusinessEntities);

    // apply custom sorting for Exact->Very High->High->Medium->Low->Very Low->No Match
    setAllScreenedEntities(allScreenings.sort(sortScreeningResult));

    if (
      (screenedBusinessEntities?.length || screenedIndividualEntities?.length) &&
      !screeningIsLoading
    ) {
      addDownloadPdfButton(true);
    }
  }, [screenedIndividualEntities, screenedBusinessEntities, screeningIsLoading]);

  useInterval(async () => {
    await getJobsStatus();
  }, jobStatusDelay);

  return (
    <div className="col-lg-12">
      <div className="row">
        <div className="col-lg-12 mt-10">
          <div className="card-title">
            <h4> </h4>
          </div>
        </div>
        <div className="col-lg-12">
          {/* Hide if the user doesn't have the appropriate cra_visible flag */}
          {/*============ CUSTOMER RISK ASSESSMENT (CRA) ===========*/}
          {currentUser?.cra_visible ? (
              <div
                className={`card accordion ${screeningIsLoading ? 'loading' : ''} ${
                  activeIndex === 'cra' ? 'active' : ''
                }`}
              >
                <CustomerRiskAssessment
                  handleOnClick={handleOnClick}
                  updateCRADetails={updateCRADetails}
                  screeningAlerts={screeningAlerts}
                  craDefaultValues={craDefaultValues}
                  companyReport={detailData}
                  companyReportHasLoaded={companyReportHasLoaded}
                />
              </div>
            ) : null
          }
        
          {/*===========================================================================*/}
          {/*========================== SCREENING RESULTS ==============================*/}
          {
            !currentUser?.screening_disabled ? (
              <div
                className={`card accordion ${screeningIsLoading ? 'loading' : ''} ${
                  activeIndex === 'screening-results' ? 'active' : ''
                }`}
              >
                <div className="accordion-heading" onClick={() => handleOnClick('screening-results')}>
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex gap-4 align-items-center">
                      <h4 className="mb-0">SCREENING RESULTS ({allScreenedEntities?.length})</h4>
                      <img
                        className="loading-icon"
                        src={require('src/assets/images/icons/spinner.svg').default}
                        alt="Loading Icon"
                      />
                      {/*<p className="btn-status-complete mb-0">COMPLETE</p>*/}
                    </div>
                    <div>
                      <img
                        className="chevron-icon"
                        src={require('src/assets/images/icons/chevron-down.svg').default}
                        alt="chevron-down icon"
                      />
                    </div>
                  </div>
                </div>
                <div className="accordion-content">
                  <ul className="batchJobsListing ">
                    {allScreenedEntities.map((screenedEntity: any) => (
                      <Link
                        to={`/batch-screening/job-entity-matches/${screenedEntity?.job_id}/${screenedEntity.id}`}
                        key={screenedEntity.id}
                        target="_blank"
                      >
                        <li
                          className="d-flex flex-wrap justify-content-between p-4 hvg-bg"
                          data-testid="job-entity-list-item"
                        >
                          <div className="d-flex flex-fill">
                            <div className="company">
                              <h6>{screenedEntity.name}</h6>
                            </div>
                            <div className="d-flex flex-nowrap gap-12 flex-fill right-fields">
                              <div className="batch-job-col">
                                <span>Customer Type</span>
                                <div className="d-flex align-items-center gap-2">
                                  <img
                                    src={
                                      screenedEntity.entity_type === EntityType.Business
                                        ? require('src/assets/images/icons/ct-business-orange.svg')
                                            .default
                                        : require('src/assets/images/icons/ct-individual-green.svg')
                                            .default
                                    }
                                    alt="User Icon"
                                  />
                                  <p className="mb-0 mt-1 color-monoIntense">
                                    {screenedEntity.entity_type === EntityType.Business
                                      ? EntityType.Business
                                      : EntityType.Individual}
                                  </p>
                                </div>
                              </div>

                              <div className="batch-job-col">
                                <span>Highest Likelihood</span>

                                <LikelyHood
                                  match={getMatchByName(screenedEntity.name, allScreenedEntities)}
                                />
                              </div>
                              <div className="batch-job-col">
                                <span>Total Matches</span>
                                <p>{screenedEntity.total_matches}</p>
                              </div>
                            </div>
                          </div>
                          <div className="d-flex align-items-center">
                            <img
                              src={require('src/assets/images/icons/chevron-right.svg').default}
                              alt="Chevron Right Icon"
                            />
                          </div>
                        </li>
                      </Link>
                    ))}
                  </ul>
                </div>
              </div>
            ) : null
          }
          {/*===========================================================================*/}
        </div>

        <div className="col-lg-12 mt-10">
          <div className="card-title">
            <h4>COMPANY DETAILS</h4>
          </div>
        </div>
        <div className="col-lg-12">
          {/*================================ SHAREHOLDERS ==============================*/}
          <Shareholder
            activeIndex={activeIndex}
            handleOnClick={handleOnClick}
            detailData={detailData}
          />
          {/*==============================================================================*/}

          {/*================================ DIRECTORS ===================================*/}
          <Director
            activeIndex={activeIndex}
            handleOnClick={handleOnClick}
            detailData={detailData}
            allScreenedEntities={allScreenedEntities}
          />
          {/*==============================================================================*/}

          {/*========================== ULTIMATE BENEFICIAL OWNERS  ==========================*/}
          <div className={`card accordion ${activeIndex === 'ubo' ? 'active' : ''}`}>
            <div className="accordion-heading" onClick={() => handleOnClick('ubo')}>
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex gap-4 align-items-center">
                  <h4 className="mb-0">
                    ULTIMATE BENEFICIAL OWNERS <span>({beneficialOwners.length})</span>
                  </h4>
                </div>
                <div>
                  <img
                    className="chevron-icon"
                    src={require('src/assets/images/icons/chevron-down.svg').default}
                    alt="chevron-down icon"
                  />
                </div>
              </div>
            </div>
            <div className="accordion-content">
              <Tabs>
                <TabList>
                  <Tab>INDIVIDUALS</Tab>
                  <Tab>BUSINESSES</Tab>
                </TabList>
                <TabPanel>
                  <div className="mt-3">
                    <table className="accordion-table sm-th-md-td">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Match</th>
                          <th>Direct Ownership</th>
                          <th>Indirect Ownership</th>
                          <th>Total Ownership</th>
                          <th>
                            {detailData.source === PROVIDER_SOURCES.DNB ? 'Address' : 'Country'}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {beneficialOwners
                          .filter(
                            (individualBeneficialOwner: any) =>
                              individualBeneficialOwner.shareholderType !== 'company',
                          )
                          .map((beneficialOwner: any) => (
                            <tr key={beneficialOwner.registrationNumber + beneficialOwner.name}>
                              <td>{beneficialOwner.name ?? DEFAULT_EMPTY_VALUE}</td>
                              <td>
                                <LikelyHood
                                  match={getMatchByName(
                                    beneficialOwner.name,
                                    screenedIndividualEntities,
                                  )}
                                />
                              </td>
                              <td>{beneficialOwner.directOwnership ?? DEFAULT_EMPTY_VALUE}</td>
                              <td>{beneficialOwner.indirectOwnership ?? DEFAULT_EMPTY_VALUE}</td>
                              <td>
                                {beneficialOwner.beneficialOwnershipPercentage ??
                                  DEFAULT_EMPTY_VALUE}
                              </td>
                              <td>
                                {detailData.source === PROVIDER_SOURCES.DNB
                                  ? beneficialOwner.address === ''
                                    ? DEFAULT_EMPTY_VALUE
                                    : beneficialOwner.address
                                  : beneficialOwner.countryCode === ''
                                  ? DEFAULT_EMPTY_VALUE
                                  : beneficialOwner.countryCode}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {
                    !!beneficialOwnersRelations.length
                    && (
                      <h6 className="mt-5">Relations</h6>
                    )
                  }

                  {
                    !!beneficialOwnersRelations.length
                    && (
                      <div className="mt-3">
                        <table className="accordion-table sm-th-md-td">
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>Description</th>
                            </tr>
                          </thead>
                          <tbody>
                            {beneficialOwnersRelations
                              .filter(
                                (individualBeneficialOwner: any) =>
                                  individualBeneficialOwner.shareholderType !== 'company',
                              )
                              .map((beneficialOwner: any) => (
                                beneficialOwner.relations.map((relation: any) => (
                                  relation.natureOfControlType.map((controlType: any) => (
                                    <tr key={beneficialOwner.registrationNumber + beneficialOwner.name}>
                                      <td className='no-text-wrap'>{beneficialOwner.name ?? DEFAULT_EMPTY_VALUE}</td>
                                      <td className='full-width'>{controlType.description ?? DEFAULT_EMPTY_VALUE}</td>
                                    </tr>
                                  ))
                                ))
                              ))}
                          </tbody>
                        </table>
                      </div>
                    )
                  }
                </TabPanel>
                <TabPanel>
                  <div className="mt-3">
                    <table className="accordion-table sm-th-md-td">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Match</th>
                          <th>Direct Ownership</th>
                          <th>Indirect Ownership</th>
                          <th>Total Ownership</th>
                          <th>
                            {detailData.source === PROVIDER_SOURCES.DNB ? 'Address' : 'Country'}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {beneficialOwners
                          .filter(
                            (companyBeneficialOwner: any) =>
                              companyBeneficialOwner.shareholderType === 'company',
                          )
                          .map((beneficialOwner: any) => (
                            <tr key={beneficialOwner.registrationNumber + beneficialOwner.name}>
                              <td>{beneficialOwner.name ?? DEFAULT_EMPTY_VALUE}</td>
                              <td>
                                <LikelyHood
                                  match={getMatchByName(
                                    beneficialOwner.name,
                                    screenedBusinessEntities,
                                  )}
                                />
                              </td>
                              <td>{beneficialOwner.directOwnership ?? DEFAULT_EMPTY_VALUE}</td>
                              <td>{beneficialOwner.indirectOwnership ?? DEFAULT_EMPTY_VALUE}</td>
                              <td>
                                {beneficialOwner.beneficialOwnershipPercentage ??
                                  DEFAULT_EMPTY_VALUE}
                              </td>
                              <td>
                                {detailData.source === PROVIDER_SOURCES.DNB
                                  ? beneficialOwner.address === ''
                                    ? DEFAULT_EMPTY_VALUE
                                    : beneficialOwner.address
                                  : beneficialOwner.countryCode === ''
                                  ? DEFAULT_EMPTY_VALUE
                                  : beneficialOwner.countryCode}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>

                  {
                    !!beneficialOwnersRelations.length
                    && (
                      <h6 className="mt-5">Relations</h6>
                    )
                  }

                  {
                    !!beneficialOwnersRelations.length 
                    && (
                      <div className="mt-3">
                        <table className="accordion-table sm-th-md-td">
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>Description</th>
                            </tr>
                          </thead>
                          <tbody>
                            {beneficialOwnersRelations
                              .filter(
                                (individualBeneficialOwner: any) =>
                                  individualBeneficialOwner.shareholderType === 'company',
                              )
                              .map((beneficialOwner: any) => (
                                beneficialOwner.relations.map((relation: any) => (
                                  relation.natureOfControlType.map((controlType: any) => (
                                    <tr key={beneficialOwner.registrationNumber + beneficialOwner.name}>
                                      <td className='no-text-wrap'>{beneficialOwner.name ?? DEFAULT_EMPTY_VALUE}</td>
                                      <td className='full-width'>{controlType.description ?? DEFAULT_EMPTY_VALUE}</td>
                                    </tr>
                                  ))
                                ))
                              ))}
                          </tbody>
                        </table>
                      </div>
                    )
                  }
                </TabPanel>
              </Tabs>
            </div>
          </div>
          {/*=================================================================================*/}

          {/*========================== OWNERSHIP STRUCTURE  ==========================*/}
          {detailData?.source === PROVIDER_SOURCES.DNB
            && detailData.companyProfile?.companyProfile?.ownershipStructure?.graph
            && detailData.companyProfile?.companyProfile?.ownershipStructure?.owners 
          ? (
            <OwnershipStructure
              activeIndex={activeIndex}
              handleOnClick={handleOnClick}
              detailData={detailData}
            />
          ) : (
            ''
          )}
          {/*=================================================================================*/}

          {/*========================== GROUP STRUCTURE ======================================*/}
          <GroupStructure
            activeIndex={activeIndex}
            handleOnClick={handleOnClick}
            detailData={detailData}
          />
          {/*=================================================================================*/}

          <FinancialInformation
            activeIndex={activeIndex}
            handleOnClick={handleOnClick}
            detailData={detailData}
          />

          {/*========================== CREDIT SUMMARY =====================================*/}
          {detailData?.source === PROVIDER_SOURCES.CREDITSAFE
          ? (
            <CreditSummary
              activeIndex={activeIndex}
              handleOnClick={handleOnClick}
              detailData={detailData}
            />
          ) : (
            ''
          )}
          {/*===============================================================================*/}

          <AdditionalInformation
            activeIndex={activeIndex}
            handleOnClick={handleOnClick}
            detailData={detailData}
          />

          {/*========================== Relevant Documents===============================*/}
          {/* wait for company profile information before loading Relevant documents */}
          {detailData?.source ? (
            <RelevantDocuments
              detailData={detailData}
              activeIndex={activeIndex}
              handleOnClick={handleOnClick}
            />
          ) : (
            ''
          )}
          {/*=================================================================================*/}
        </div>
      </div>
    </div>
  );
};

export default CompanyDetails;
